import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import {
  loadGoogleMaps,
  onGoogleMapsAuthFailure,
} from "../services/googleMapsLoader";

// Default center used before a location is chosen (roughly central Europe).
const DEFAULT_CENTER = { lat: 51.1657, lng: 10.4515 };

// AdvancedMarkerElement requires a Map ID. Configure a real cloud Map ID via
// VITE_GOOGLE_MAPS_MAP_ID; DEMO_MAP_ID is Google's built-in dev fallback.
const MAP_ID = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID";

export default function MapPicker({ location, onSelectLocation }) {
  const { t } = useLanguage();
  const mapRef = useRef(null);
  const mapObjRef = useRef(null);
  const markerRef = useRef(null);
  const [unavailable, setUnavailable] = useState(false);

  // Keep the latest callback/location without re-initialising the map, so the
  // dragend handler never fires with a stale closure.
  const onSelectRef = useRef(onSelectLocation);
  onSelectRef.current = onSelectLocation;
  const locationRef = useRef(location);
  locationRef.current = location;

  // Initialise the map once, when Google Maps is ready.
  useEffect(() => {
    let cancelled = false;

    // Only runs once loadGoogleMaps() has resolved, which means `marker` is
    // imported and the container is mounted — so no readiness check here. If
    // either assumption ever breaks, throwing lands in the .catch below and the
    // map hides itself, rather than leaving a silent empty grey box.
    const init = () => {
      if (cancelled || mapObjRef.current) return;

      // Coordinates from the API arrive as strings (Rails serializes decimal
      // columns as strings); Google Maps requires numbers or it renders blank.
      const lat = Number(location?.lat);
      const lng = Number(location?.lng);
      const hasPin = Number.isFinite(lat) && Number.isFinite(lng);
      const center = hasPin ? { lat, lng } : DEFAULT_CENTER;

      const map = new google.maps.Map(mapRef.current, {
        center,
        zoom: hasPin ? 17 : 4,
        mapId: MAP_ID,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: hasPin ? map : null,
        position: center,
        gmpDraggable: true,
      });

      marker.addListener("dragend", () => {
        // AdvancedMarkerElement exposes .position (LatLng or LatLngLiteral)
        // rather than getPosition(); normalise to plain numbers.
        const pos = marker.position;
        const dLat = typeof pos.lat === "function" ? pos.lat() : pos.lat;
        const dLng = typeof pos.lng === "function" ? pos.lng() : pos.lng;
        // Dragging only adjusts coordinates; the address text keeps what the
        // merchant searched (no reverse-geocoding, so no Geocoding API cost and
        // no auto-generated place/brand names).
        onSelectRef.current({
          address: locationRef.current?.address,
          lat: dLat,
          lng: dLng,
        });
      });

      mapObjRef.current = map;
      markerRef.current = marker;

      // With no pin yet, center on the merchant's location (city zoom) instead
      // of the continent fallback. Only applies while nothing has been chosen.
      if (!hasPin && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (cancelled || locationRef.current?.lat) return;
            map.setCenter({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
            map.setZoom(12);
          },
          () => {},
          { timeout: 10000, maximumAge: 600000 },
        );
      }
    };

    // Maps is loaded on demand (see services/googleMapsLoader) rather than from
    // index.html. If it never arrives, render nothing instead of an empty grey
    // box — AddressAutocomplete surfaces the explanation and the retry, and it
    // fails on the same shared promise, so it is always there to do it.
    loadGoogleMaps()
      .then(init)
      .catch((error) => {
        if (cancelled) return;
        console.warn("Map picker unavailable:", error.message);
        setUnavailable(true);
      });

    // Google rejects the key when the map renders, i.e. after init() succeeded —
    // hide the grey error tile it leaves behind and let AddressAutocomplete carry
    // the explanation.
    const unsubscribe = onGoogleMapsAuthFailure(() => {
      if (cancelled) return;
      setUnavailable(true);
    });

    return () => {
      cancelled = true;
      unsubscribe();
      if (markerRef.current) {
        google.maps.event.clearInstanceListeners(markerRef.current);
        markerRef.current.map = null;
      }
      // Clear the refs so a remount (e.g. StrictMode's double-invoke in dev)
      // re-initialises the map and re-requests geolocation instead of bailing.
      mapObjRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter and drop the pin when the location changes from outside (e.g. the
  // address search selecting a place).
  useEffect(() => {
    if (!mapObjRef.current || !markerRef.current) return;
    const lat = Number(location?.lat);
    const lng = Number(location?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const pos = { lat, lng };
    mapObjRef.current.setCenter(pos);
    mapObjRef.current.setZoom(17);
    markerRef.current.position = pos;
    markerRef.current.map = mapObjRef.current;
  }, [location?.lat, location?.lng]);

  if (unavailable) return null;

  return (
    <div className="space-y-1">
      <div
        ref={mapRef}
        className="h-64 w-full rounded-md border border-gray-300"
      />
      <p className="text-xs text-gray-500">
        {t("settings.branches.form.mapHint")}
      </p>
    </div>
  );
}
